import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { fetchProjects, fetchSettings, fetchClips } from "../lib/api";
import { toEmbedSrc } from "../lib/embed";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import RecentVisuals from "../components/RecentVisuals";
import RecentlyUpdatedBadge from "../components/RecentlyUpdatedBadge";

const HERO_BG_FALLBACK =
    "https://static.prod-images.emergentagent.com/jobs/075f11f9-3d6d-4525-bb90-f765daee5e5b/images/f353589c3043814794bdbb781d9f3b01e7112637691e9f314a0478a9a290a365.png";

const Home = () => {
    const [featured, setFeatured] = useState([]);
    const [settings, setSettings] = useState(null);
    const [reelOpen, setReelOpen] = useState(false);
    const [marqueeImages, setMarqueeImages] = useState([]);
    const heroVideoRef = useRef(null);
    const thrillerAudioRef = useRef(null);
    const audioPlayedRef = useRef(false);
    const [pushingIn, setPushingIn] = useState(false);

    // Hero choreography — anchored to the video's first `playing` event so
    // every event lands at a known wall-clock offset, independent of
    // autoplay quirks, network latency, or `timeupdate` cadence.
    //
    // Timeline (relative to first frame on screen):
    //     t = 7.0s  → fade video volume to 0 + start thriller MP3
    //     t = 14.0s → JUMP CUT to push-in (mid-loop 2, after the car re-reads)
    //     t = 23.0s → revert push-in to normal Ken Burns
    //
    // Robustness:
    //   - Master timer fires regardless of mute state. Both rain-duck and
    //     thriller-play are attempted; thriller retries on user gestures
    //     until it actually starts (autoplay-with-sound is often blocked
    //     on first visit until the user interacts).
    //   - Push-in is a wall-clock CSS class flip — no dependency on
    //     `timeupdate` loop detection that could miss a wrap.
    useEffect(() => {
        const v = heroVideoRef.current;
        const m = thrillerAudioRef.current;
        if (!v) return undefined;

        const cleanupTimers = [];
        let clockStarted = false;
        let thrillerCueDue = false;
        let thrillerPlaying = false;

        const playThriller = () => {
            if (!thrillerCueDue || thrillerPlaying || !m) return;
            try {
                m.currentTime = 0;
                m.volume = 1;
                const p = m.play();
                if (p && typeof p.then === "function") {
                    p.then(() => {
                        thrillerPlaying = true;
                    }).catch(() => {
                        /* gesture required — retried on next interaction */
                    });
                } else {
                    thrillerPlaying = true;
                }
            } catch {
                /* noop */
            }
        };

        const fadeVideoVolume = () => {
            const fadeDurMs = 1080;
            const steps = 22;
            let i = 0;
            const stepMs = fadeDurMs / steps;
            const startVol = v.volume || 0.85;
            const interval = setInterval(() => {
                i += 1;
                try {
                    v.volume = Math.max(0, startVol * (1 - i / steps));
                } catch {
                    /* noop */
                }
                if (i >= steps) {
                    clearInterval(interval);
                    try {
                        v.muted = true;
                        v.volume = 1;
                    } catch {
                        /* noop */
                    }
                }
            }, stepMs);
        };

        const startMasterClock = () => {
            if (clockStarted) return;
            clockStarted = true;

            // t = 7.0s — rain ducks, thriller cue plays.
            cleanupTimers.push(
                setTimeout(() => {
                    thrillerCueDue = true;
                    playThriller();
                    fadeVideoVolume();
                }, 7000),
            );

            // t = 14.0s — JUMP CUT to push-in.
            cleanupTimers.push(
                setTimeout(() => {
                    setPushingIn(true);
                }, 14000),
            );

            // t = 23.0s — revert push-in (after the ~9s animation completes).
            cleanupTimers.push(
                setTimeout(() => {
                    setPushingIn(false);
                }, 23000),
            );
        };

        // Anchor: the first time the video actually starts painting frames.
        const onPlaying = () => startMasterClock();
        v.addEventListener("playing", onPlaying);
        // Safety net: if `playing` somehow never fires, start the clock
        // 1.5s after mount so the choreography still happens.
        cleanupTimers.push(setTimeout(startMasterClock, 1500));

        const tryUnmuteVideo = () => {
            if (audioPlayedRef.current) return;
            audioPlayedRef.current = true;
            try {
                v.muted = false;
                v.volume = 0.85;
            } catch {
                /* noop */
            }
            const p = v.play();
            if (p && typeof p.then === "function") {
                p.catch(() => {
                    audioPlayedRef.current = false;
                    try {
                        v.muted = true;
                        v.play().catch(() => {});
                    } catch {
                        /* noop */
                    }
                });
            }
        };

        // 1) Try unmuted autoplay immediately (may be blocked on first visit).
        tryUnmuteVideo();

        // 2) Any user gesture: retry unmute AND retry thriller cue.
        const triggers = ["pointerdown", "keydown", "scroll", "touchstart"];
        const handler = () => {
            if (!audioPlayedRef.current) tryUnmuteVideo();
            playThriller();
            if (audioPlayedRef.current && thrillerPlaying) {
                triggers.forEach((t) =>
                    window.removeEventListener(t, handler),
                );
            }
        };
        triggers.forEach((t) =>
            window.addEventListener(t, handler, { passive: true }),
        );

        return () => {
            v.removeEventListener("playing", onPlaying);
            triggers.forEach((t) => window.removeEventListener(t, handler));
            cleanupTimers.forEach((id) => clearTimeout(id));
            if (m) {
                try {
                    m.pause();
                    m.currentTime = 0;
                } catch {
                    /* noop */
                }
            }
        };
    }, []);

    useEffect(() => {
        fetchProjects({ featured: true })
            .then(setFeatured)
            .catch(() => {});
        fetchSettings()
            .then(setSettings)
            .catch(() => {});
        // Marquee should always have enough thumbnails to rotate all 3 labels.
        // Strategy: featured clips first, then top up with general clips (deduped).
        (async () => {
            try {
                const [feat, general] = await Promise.all([
                    fetchClips({ featured: true, limit: 14 }),
                    fetchClips({ limit: 14 }),
                ]);
                const seen = new Set();
                const ordered = [...feat, ...general].filter((c) => {
                    if (seen.has(c.id)) return false;
                    seen.add(c.id);
                    return c.thumbnail_url || c.poster_url;
                });
                setMarqueeImages(
                    ordered.map((c) => ({
                        src: c.poster_url || c.thumbnail_url,
                        label: c.title || "",
                    })),
                );
            } catch {
                /* fail silently — marquee is decorative */
            }
        })();
    }, []);

    const reelSrc = toEmbedSrc(settings?.reel_embed_url);
    const heroBg = settings?.hero_bg_url || HERO_BG_FALLBACK;

    return (
        <main data-testid="home-page">
            {/* Thriller cue — fades in as the rain fades out (single play per
                session). The MP3 carries its own 1.6s fade-in + 3s fade-out. */}
            <audio
                ref={thrillerAudioRef}
                src="/thriller-cue.mp3"
                preload="auto"
                aria-hidden
                data-testid="hero-thriller-audio"
            />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative min-h-screen w-full overflow-hidden flex items-end"
            >
                {/* Static cinematic still — always present, acts as the "reel placeholder" the video gives way to */}
                <div
                    className="absolute inset-0 bg-cover bg-center scale-105"
                    style={{ backgroundImage: `url(${heroBg})` }}
                    aria-hidden
                />

                {/* Sora-generated cinematic loop — dark Sacramento street ambient intro */}
                <video
                    key="hero-intro-video"
                    ref={heroVideoRef}
                    data-testid="hero-intro-video"
                    src="/hero-intro.mp4"
                    poster="/hero-intro-poster.jpg"
                    autoPlay
                    muted
                    playsInline
                    loop
                    preload="auto"
                    className={`absolute inset-0 w-full h-full object-cover hero-intro-video ${
                        pushingIn ? "is-pushing-in" : ""
                    }`}
                    aria-hidden
                />

                <div
                    className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black"
                    aria-hidden
                />

                {/* Cinematic vignette — soft radial darkening at edges,
                    evokes a projector / anamorphic lens falloff. Sits above
                    the gradient but below interactive content. */}
                <div
                    data-testid="hero-vignette"
                    className="absolute inset-0 pointer-events-none hero-vignette"
                    aria-hidden
                />

                <div className="relative z-10 mx-auto max-w-[1500px] w-full px-6 sm:px-10 lg:px-14 pb-20 sm:pb-28">
                    <Reveal delay={100}>
                        <p
                            data-testid="hero-eyebrow"
                            className="text-label text-foreground/70 mb-6"
                        >
                            Featured Reel{reelSrc ? "" : " — coming soon"}
                        </p>
                    </Reveal>

                    <Reveal delay={250}>
                        <h1
                            data-testid="hero-title"
                            className="font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl"
                        >
                            John Alexander
                            <br />
                            <span className="italic text-muted-foreground">jimenez</span>
                        </h1>
                    </Reveal>

                    <Reveal delay={400}>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-6 text-label text-foreground/80"
                        >
                            Award-Winning Cinematographer&nbsp;&nbsp;•&nbsp;&nbsp;Director
                        </p>
                    </Reveal>

                    <Reveal delay={550}>
                        <p
                            data-testid="hero-supporting"
                            className="mt-8 max-w-xl text-base sm:text-lg text-foreground/70 leading-relaxed"
                        >
                            Over two decades crafting stories through{" "}
                            <strong className="font-semibold text-foreground/90">
                                cinematography
                            </strong>
                            , direction, editing, and visual storytelling.
                        </p>
                    </Reveal>

                    <Reveal delay={700}>
                        <div className="mt-12 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                onClick={() => setReelOpen(true)}
                                data-testid="hero-play-reel"
                                className="group inline-flex items-center gap-3 bg-foreground text-background px-6 py-4 hover:bg-foreground/90 transition-colors duration-500"
                            >
                                <Play size={16} strokeWidth={1.5} className="fill-current" />
                                <span className="text-label">Play Reel</span>
                            </button>
                            <Link
                                to="/work"
                                data-testid="hero-view-portfolio"
                                className="text-label text-foreground/80 hover:text-foreground underline-anim ml-2"
                            >
                                View Portfolio
                            </Link>
                            <Link
                                to="/about"
                                data-testid="hero-about"
                                className="text-label text-foreground/80 hover:text-foreground underline-anim"
                            >
                                About
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-contact"
                                className="text-label text-foreground/80 hover:text-foreground underline-anim"
                            >
                                Contact
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MARQUEE — Selected Frames · Recent Work · Ongoing Projects */}
            {marqueeImages.length > 0 && (
                <div className="relative">
                    <Marquee
                        items={marqueeImages}
                        labels={[
                            "Selected Frames",
                            "Recent Work",
                            "Ongoing Projects",
                        ]}
                        speedSeconds={110}
                    />
                    <RecentlyUpdatedBadge
                        mode={settings?.badge_mode || "auto"}
                    />
                </div>
            )}

            {/* SELECTED WORK */}
            <section
                data-testid="home-selected-work"
                className="relative mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14 py-32 sm:py-40"
            >
                <div className="flex items-end justify-between mb-16">
                    <Reveal>
                        <div>
                            <p className="text-label text-muted-foreground mb-4">
                                Selected Work
                            </p>
                            <h2 className="font-serif-display text-3xl sm:text-5xl tracking-tight max-w-2xl">
                                Years behind the lens. A few moments gathered here.
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal delay={150}>
                        <Link
                            to="/work"
                            data-testid="home-view-all"
                            className="hidden sm:inline-flex items-center gap-2 text-label text-muted-foreground hover:text-foreground transition-colors"
                        >
                            View All <ArrowRight size={14} strokeWidth={1.25} />
                        </Link>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-16">
                    {featured.slice(0, 3).map((p, i) => (
                        <Reveal
                            key={p.id}
                            delay={i * 120}
                            className={
                                i === 0
                                    ? "md:col-span-8"
                                    : i === 1
                                      ? "md:col-span-4 md:mt-24"
                                      : "md:col-span-7 md:col-start-3"
                            }
                        >
                            <Link
                                to={`/work/${p.id}`}
                                data-testid={`featured-card-${p.id}`}
                                className="block group"
                            >
                                <div className="overflow-hidden bg-muted aspect-[16/10]">
                                    <img
                                        src={p.thumbnail_url}
                                        alt={p.title}
                                        loading="lazy"
                                        style={
                                            p.thumbnail_position
                                                ? { objectPosition: p.thumbnail_position }
                                                : undefined
                                        }
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                    />
                                </div>
                                <div className="mt-5 flex items-baseline justify-between gap-6">
                                    <h3 className="font-serif-display text-xl sm:text-2xl tracking-tight">
                                        {p.title}
                                    </h3>
                                    <span className="text-label text-muted-foreground">
                                        {p.year}
                                    </span>
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground tracking-wide">
                                    {p.role} — {p.category}
                                </p>
                            </Link>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-20 sm:hidden">
                    <Link
                        to="/work"
                        className="inline-flex items-center gap-2 text-label text-muted-foreground"
                    >
                        View All <ArrowRight size={14} strokeWidth={1.25} />
                    </Link>
                </Reveal>
            </section>

            {/* RECENT VISUALS / SOCIAL WORK */}
            <RecentVisuals />

            {/* AWARDS TEASER */}
            <section
                data-testid="home-awards-teaser"
                className="relative border-t border-border"
            >
                <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14 py-28 sm:py-36 grid lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-5">
                        <p className="text-label text-muted-foreground mb-4">Awards</p>
                        <h2 className="font-serif-display text-3xl sm:text-5xl tracking-tight">
                            Recognized for his craft.
                        </h2>
                    </Reveal>
                    <Reveal delay={200} className="lg:col-span-7 lg:pt-3">
                        <ul className="divide-y divide-border">
                            {[
                                {
                                    year: "2014",
                                    title: "Local Vanguard Award",
                                    org: "Sacramento Film Festival",
                                },
                                {
                                    year: "2000",
                                    title: "Emerging Artist Award — Cinematography",
                                    org: "Sacramento Metropolitan Arts Commission",
                                },
                            ].map((a) => (
                                <li
                                    key={a.year}
                                    className="py-6 grid grid-cols-[80px_1fr] gap-6 items-baseline"
                                >
                                    <span className="text-label text-muted-foreground">
                                        {a.year}
                                    </span>
                                    <div>
                                        <p className="font-serif-display text-xl sm:text-2xl tracking-tight">
                                            {a.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            {a.org}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/awards"
                            data-testid="home-awards-link"
                            className="inline-flex items-center gap-2 mt-10 text-label text-muted-foreground hover:text-foreground"
                        >
                            Full timeline <ArrowRight size={14} strokeWidth={1.25} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* REEL MODAL */}
            {reelOpen && (
                <div
                    data-testid="reel-modal"
                    className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
                    onClick={() => setReelOpen(false)}
                >
                    <button
                        type="button"
                        onClick={() => setReelOpen(false)}
                        data-testid="reel-modal-close"
                        className="absolute top-6 right-6 text-label text-foreground/80 hover:text-foreground"
                    >
                        Close ×
                    </button>
                    <div
                        className="w-full max-w-5xl aspect-video bg-muted"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {reelSrc ? (
                            <iframe
                                title="Featured Reel"
                                src={reelSrc}
                                className="w-full h-full"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-center p-10">
                                <p className="text-label text-muted-foreground mb-4">
                                    Reel Placeholder
                                </p>
                                <p className="font-serif-display text-2xl sm:text-3xl max-w-xl leading-snug">
                                    A reel link can be added at any time from
                                    the admin panel — no redesign required.
                                </p>
                                <p className="text-sm text-muted-foreground mt-6">
                                    Paste a Vimeo or YouTube URL in&nbsp;
                                    <Link
                                        to="/admin"
                                        className="underline underline-offset-4"
                                        onClick={() => setReelOpen(false)}
                                    >
                                        /admin → Settings
                                    </Link>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </main>
    );
};

export default Home;
